import React from "react";
import { useSeo } from "../lib/useSeo";
import { useLang } from "../lib/i18n";
import { COMPANY } from "../lib/constants";

/* Shared reading-column layout for the legal / policy pages (English only). */
export const LegalLayout = ({ title, seo, sections, children }) => {
  const { lang } = useLang();
  useSeo(seo);

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-[1200px] px-5 pb-24 pt-32 lg:px-8 lg:pt-40">
        <div className="lg:flex lg:gap-16">
          <article className="legal-prose w-full max-w-[760px]">
            <h1 className="font-display text-4xl font-black tracking-tight text-navy sm:text-5xl" data-testid="legal-title">{title}</h1>
            <p className="mt-3 text-sm font-semibold text-slate-500" data-testid="legal-updated">Last updated: {COMPANY.legalUpdated}</p>
            {lang === "hi" && (
              <p className="mt-6 rounded-xl bg-slate-50 px-4 py-3 text-sm font-semibold text-navy" data-testid="legal-hi-notice">
                यह पृष्ठ अंग्रेज़ी में है
              </p>
            )}
            <div className="mt-10">{children}</div>
          </article>

          <nav className="hidden shrink-0 lg:block" aria-label="On this page">
            <div className="sticky top-32 w-56">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">On this page</p>
              <ul className="mt-4 space-y-2.5 border-l border-navy/10 pl-4 text-sm" data-testid="legal-toc">
                {sections.map((sec) => (
                  <li key={sec.id}>
                    <a href={`#${sec.id}`} className="text-slate-600 transition-colors hover:text-electric-ink">{sec.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
};

export const LegalSection = ({ id, heading, children }) => (
  <section id={id} className="scroll-mt-32 pt-8 first:pt-0" data-testid={`legal-section-${id}`}>
    <h2 className="font-display text-lg font-bold text-navy md:text-lg">{heading}</h2>
    <div className="mt-3 space-y-4 text-[17px] leading-[1.75] text-slate-700">{children}</div>
  </section>
);

export const LegalTable = ({ head, rows, testId }) => (
  <div className="my-6 overflow-x-auto rounded-2xl border border-navy/10" data-testid={testId}>
    <table className="w-full border-collapse text-left text-[15px]">
      <thead>
        <tr className="bg-slate-50">
          {head.map((h) => (
            <th key={h} className="border-b border-navy/10 px-4 py-3 font-display text-sm font-bold text-navy">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="align-top">
            <th scope="row" className="border-b border-navy/[0.06] px-4 py-3 text-left font-semibold leading-relaxed text-navy">{r[0]}</th>
            {r.slice(1).map((c, j) => (
              <td key={j} className="border-b border-navy/[0.06] px-4 py-3 leading-relaxed text-slate-700">{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
